<script setup lang="ts">
// What turning Orpheus on agrees to (v3 `OrpheusConsentCopy.vue`): four numbered points, a rule,
// and the agreement sentence. Two points and the footer change with the payment choice — the
// own-key text names the provider and its billing; the band credit numbers are admin-tunable and
// read from `orpheus-settings`, never written here.
import { computed } from 'vue'

import type { OrpheusConsentVariant } from '../../models/orpheusConsent.types'
import type { OrpheusSettingsDto } from '../../models/orpheusKey.types'
import { bandRange, providerName } from '../../service/orpheusApiKey'

import OrpheusLegalLink from './OrpheusLegalLink.vue'

import { useI18n } from '@/shared/i18n'

const props = withDefaults(
  defineProps<{
    variant?: OrpheusConsentVariant
    provider?: string | null
    bands?: OrpheusSettingsDto['bands'] | null
  }>(),
  { variant: 'orphex_credits', provider: null, bands: null },
)

const { t } = useI18n()
const K = 'orpheus.consent.copy'

const isOwnKey = computed(() => props.variant === 'own_key')
const name = computed(() => providerName(props.provider) ?? t('orpheus.apiKey.theProvider'))

const points = computed(() => {
  const reads = { lead: t(`${K}.reads.lead`), rest: t(`${K}.reads.rest`) }
  const neverWrites = { lead: t(`${K}.neverWrites.lead`), rest: t(`${K}.neverWrites.rest`) }
  if (!isOwnKey.value) {
    return [
      reads,
      { lead: t(`${K}.shares.lead`), rest: t(`${K}.shares.rest`) },
      neverWrites,
      { lead: t(`${K}.credits.lead`), rest: t(`${K}.credits.rest`) },
    ]
  }

  const ownRange = bandRange(props.bands?.own_key)
  const orphexRange = bandRange(props.bands?.orphex)
  return [
    reads,
    {
      lead: t(`${K}.ownKeyShares.lead`, { name: name.value }),
      rest: t(`${K}.ownKeyShares.rest`, { name: name.value }),
    },
    neverWrites,
    {
      lead: t(`${K}.ownKeyBilling.lead`, { name: name.value }),
      rest:
        ownRange && orphexRange
          ? t(`${K}.ownKeyBilling.restRange`, { own: ownRange, orphex: orphexRange })
          : t(`${K}.ownKeyBilling.rest`),
    },
  ]
})
</script>

<template>
  <div class="orpheus-consent-copy flex flex-col gap-5">
    <ul class="m-0 flex list-none flex-col gap-5 p-0">
      <li v-for="(point, index) in points" :key="point.lead" class="flex items-start gap-3">
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-sm font-semibold text-muted"
          aria-hidden="true"
        >
          {{ index + 1 }}
        </span>
        <p class="m-0 text-sm text-fg">
          <strong class="font-semibold">{{ point.lead }}</strong> {{ point.rest }}
        </p>
      </li>
    </ul>

    <hr class="m-0 border-0 border-t border-border" />

    <i18n-t
      v-if="isOwnKey"
      keypath="orpheus.consent.copy.footerOwnKey"
      tag="p"
      class="m-0 text-xs text-muted"
      scope="global"
      data-testid="orpheus-consent-footer-own-key"
    >
      <template #name>{{ name }}</template>
      <template #terms>
        <OrpheusLegalLink slug="terms-of-service" :label="t('orpheus.consent.copy.terms')" />
      </template>
    </i18n-t>
    <i18n-t
      v-else
      keypath="orpheus.consent.copy.footer"
      tag="p"
      class="m-0 text-xs text-muted"
      scope="global"
    >
      <template #privacy>
        <OrpheusLegalLink slug="privacy-policy" :label="t('orpheus.consent.copy.privacy')" />
      </template>
      <template #terms>
        <OrpheusLegalLink slug="terms-of-service" :label="t('orpheus.consent.copy.terms')" />
      </template>
    </i18n-t>
  </div>
</template>
